import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Phone, Mail, Search, X } from "lucide-react";

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });

  const load = async () => {
    const r = await api.get("/admin/customers");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Nume si telefon obligatorii"); return; }
    try {
      await api.post("/admin/customers", form);
      toast.success("Client adaugat");
      setForm({ name: "", phone: "", email: "", notes: "" });
      setOpen(false);
      load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const remove = async (id) => {
    if (!confirm("Sterge clientul?")) return;
    await api.delete(`/admin/customers/${id}`);
    toast.success("Sters");
    load();
  };

  const filtered = items.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.phone.toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.notes || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl uppercase">Clienti</h1>
          <p className="text-sm text-neutral-400 mt-1">Evidenta clientilor care vin in atelier</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none">
              <Plus className="w-4 h-4 mr-2" /> Client nou
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
            <DialogHeader><DialogTitle className="font-display uppercase">Client nou</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Nume *"><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Telefon *"><Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Email"><Input data-testid="customer-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Note"><Textarea data-testid="customer-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <DialogFooter>
                <Button type="submit" data-testid="save-customer-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">Salveaza</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="customer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cauta dupa nume, telefon, email..."
            className="bg-neutral-900 border-neutral-800 rounded-none pl-10 pr-10 h-11 focus:border-[#00E5FF]"
          />
          {search && (
            <button data-testid="customer-search-clear" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#00E5FF]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="text-xs text-neutral-500 uppercase tracking-wider">
          {filtered.length} / {items.length}
        </div>
      </div>

      <div className="bg-neutral-950 border border-neutral-800">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 border-b border-neutral-800">
            <tr className="text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="p-4">Nume</th>
              <th className="p-4">Contact</th>
              <th className="p-4 hidden md:table-cell">Note</th>
              <th className="p-4">Actiuni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="4" className="p-8 text-center text-neutral-500">{items.length === 0 ? "Niciun client inca" : "Niciun rezultat"}</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-neutral-900 hover:bg-neutral-900/50">
                <td className="p-4 font-medium">
                  <Link to={`/admin/clienti/${c.id}`} className="hover:text-[#00E5FF] inline-flex items-center gap-1">
                    {c.name}
                  </Link>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2 text-neutral-300"><Phone className="w-3 h-3 text-[#00E5FF]" /> {c.phone}</div>
                  {c.email && <div className="flex items-center gap-2 text-neutral-400 text-xs mt-1"><Mail className="w-3 h-3 text-[#00E5FF]" /> {c.email}</div>}
                </td>
                <td className="p-4 hidden md:table-cell text-neutral-400 max-w-xs truncate">{c.notes || "-"}</td>
                <td className="p-4">
                  <button data-testid={`delete-customer-${c.id}`} onClick={() => remove(c.id)} className="text-neutral-500 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {children}
  </div>
);
